import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { MainLayout } from "../MainLayout";
import { useCompany } from "../../contexts/CompanyContext";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../../lib/supabase_client";
import { useSession } from "../../contexts/SessionContext";
import { downloadXBRL } from "../../services/xbrlService";
import { SgXBRLPromoBanner } from "./SgXBRLPromoBanner";
import { SgOnboardingTour } from "./SgOnboardingTour";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import '../../styles/filing.css';
import '../../styles/page-base.css';
import '../../styles/company-profile.css';
import { pdfjs } from 'react-pdf';
import { AnnouncementsBanner } from '../AnnouncementsBanner';
import { sanitizeUploadFilename } from '../../utils/sanitizeUploadFilename';
import { uploadFilingPdfV5 } from '../../utils/uploadFilingPdfV5';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;
if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  console.warn('PDF.js worker not configured');
}
dayjs.extend(relativeTime);

interface MonthlySpend {
  month: string;
  amount: number;
}

// Processing steps mapping (matches 1.0 submissions.html lines 486-497)
const PROCESSING_STEPS: Record<string, number> = {
  'upload': 5,
  'raw_upload': 5,
  'chunk_processor': 20,
  'gpt_analyzer': 25,
  'taxonomy_generator': 30,
  'form_generator': 35,
  'preprocess_data': 40,
  'mapping': 65,
  'write_form': 85,
  'combine_json': 100
};

interface FilingDocument {
  id: string;
  company_name: string;
  filing_type_code?: string | null;
  filing_type_name?: string | null;
  year_end: string;
  status: "draft" | "processing" | "submitted" | "converted" | "archived" | "validated" | "completed" | "failed";
  created_at: string;
  created_by?: string | null;
  created_by_name?: string | null;
  updated_at: string;
  updated_by?: string | null;
  updated_by_name?: string | null;
  group_id?: string | null;
  pinned_at?: string | null;
  processing_status?: {
    status?: string;
    current_step?: string;
    last_updated?: string;
  };
}

interface Product {
  id: string;
  name: string;
  description: string | null;
  credit_amount: number;
  price: number;
  currency: string;
  is_active: boolean;
  is_promotional: boolean;
  country_iso: string;
  referral_bonus_amount: number;
  promo_code: number;
  promotional_description: string | null;
  one_time_purchase: boolean;
}

interface Group {
  id: string;
  name: string;
  description?: string;
  member_count?: number;
}

const getStatusColor = (status: string) => {
  const base: React.CSSProperties = {
    minWidth: 90,
    height: 24,
    padding: '0 8px',
    borderRadius: 9999,
    fontSize: 10,
    fontWeight: 500, // was 700
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  switch (status) {
    case 'validated':
      return { ...base, backgroundColor: 'rgba(88,232,203,0.22)', borderColor: 'rgba(88,232,203,0.55)', color: '#065f53' };
    case 'completed':
      return { ...base, backgroundColor: 'rgba(0,174,239,0.14)', borderColor: 'rgba(0,174,239,0.35)', color: 'var(--theme-600)' };
    case 'converted':
      return { ...base, backgroundColor: 'rgba(199,100,244,0.18)', borderColor: 'rgba(199,100,244,0.45)', color: '#5b1f7a' };
    case 'submitted':
      return { ...base, backgroundColor: 'rgba(0,174,239,0.14)', borderColor: 'rgba(0,174,239,0.35)', color: 'var(--theme-600)' };
    case 'processing':
    case 'archived':
    case 'failed':
      return { ...base, backgroundColor: 'rgba(244,100,100,0.20)', borderColor: 'rgba(244,100,100,0.55)', color: '#7a1f1f' };
    case 'draft':
    default:
      return { ...base, backgroundColor: 'var(--bg-white-dark)', borderColor: 'var(--bg-white-darker)', color: 'var(--dark)' };
  }
};

const formatDate = (dateString: string) => {
  return dayjs(dateString).fromNow(); // Accurate: "a few seconds ago", "2 minutes ago", "3 hours ago", "2 days ago", etc.
};

const getSgFilingTypeLabel = (code?: string) => {
  if (code === 'FULL-XBRL') return 'Full';
  if (code === 'SIMPLIFIED-XBRL') return 'Simplified';
  return null;
};

const getSgFilingTypeDisplay = (doc: FilingDocument) => {
  if (doc.filing_type_code === 'FULL-XBRL') return 'Full XBRL';
  if (doc.filing_type_code === 'SIMPLIFIED-XBRL') return 'Simplified';
  return doc.filing_type_name || '-';
};

export default function SgAllFilingPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<FilingDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFilingsCount, setTotalFilingsCount] = useState(0);
  const [showPdfPageCountModal, setShowPdfPageCountModal] = useState(false);
  const [pendingPdfFile, setPendingPdfFile] = useState<{file: File, pageCount: number} | null>(null);
  const [checkingPdfPages, setCheckingPdfPages] = useState(false);

  // Auto-focus search input when expanded
  useEffect(() => {
    if (searchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchExpanded]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { companyId } = useParams<{ companyId: string }>();
  
  // Set page title
  useEffect(() => {
    document.title = 'Rapid Tagger | Filings';
    return () => {
      document.title = 'Rapid Tagger';
    };
  }, []);

  const { selectedCompany, setSelectedCompany, userCompanies } = useCompany();
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [purchasedProductIds, setPurchasedProductIds] = useState<Set<string>>(new Set());
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [creditBalanceLoading, setCreditBalanceLoading] = useState(true);
  const { user, isUserAdmin, permissions } = useSession();
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [userGroupIds, setUserGroupIds] = useState<string[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [showGroupAssignmentModal, setShowGroupAssignmentModal] = useState<string | null>(null);
  const [tempSelectedGroups, setTempSelectedGroups] = useState<string[]>([]);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadGroupId, setUploadGroupId] = useState<string | null>(null);
  const [uploadFilingTypeId, setUploadFilingTypeId] = useState<string>('');
  const [filingTypes, setFilingTypes] = useState<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [downloadingFilingId, setDownloadingFilingId] = useState<string | null>(null);
  const [showDownloadTipModal, setShowDownloadTipModal] = useState(false);
  const [pendingDownloadDoc, setPendingDownloadDoc] = useState<FilingDocument | null>(null);
  const [selectedFilings, setSelectedFilings] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<string | null>(null); // null = all statuses
  const [createdFrom, setCreatedFrom] = useState(''); // YYYY-MM-DD, empty = no lower bound
  const [createdTo, setCreatedTo] = useState(''); // YYYY-MM-DD, empty = no upper bound
  const [draftStatusFilter, setDraftStatusFilter] = useState<string | null>(null);
  const [draftCreatedFrom, setDraftCreatedFrom] = useState('');
  const [draftCreatedTo, setDraftCreatedTo] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const [showArchived, setShowArchived] = useState<boolean>(false); // false = active, true = archived
  const [openActionMenu, setOpenActionMenu] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false); // Separate state for manual refresh button
  const [credit, setCredit] = useState<number>(0);
  const [currencyBalance, setCurrencyBalance] = useState<number>(0); 
  const [showCreditDeductionModal, setShowCreditDeductionModal] = useState(false);
  const [pendingDeductions, setPendingDeductions] = useState<{
    freeCredit: number;
    currencyBalance: number;
  }>({ freeCredit: 0, currencyBalance: 0 });
  const [pendingUpload, setPendingUpload] = useState<{file: File, filingTypeId: string, groupId: string | null} | null>(null);
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [allGroupsLoading, setAllGroupsLoading] = useState(false);
  const [monthlySpend, setMonthlySpend] = useState<MonthlySpend[]>([]);
  const [totalConversions, setTotalConversions] = useState(0);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [referralCode, setReferralCode] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [hoveredMonthIndex, setHoveredMonthIndex] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] = useState(false);
  const [insufficientInfo, setInsufficientInfo] = useState<{
    currentFreeCredit: number;
    currentCurrencyBalance: number;
    requiredCurrencyBalance: number;
  } | null>(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showUnarchiveConfirm, setShowUnarchiveConfirm] = useState(false);
  const [showDeleteFilingConfirm, setShowDeleteFilingConfirm] = useState<string | null>(null);


  const fetchCreditBalance = useCallback(async () => {
    if (!selectedCompany?.company_id) {
      setCreditBalanceLoading(false);
      return;
    }
  
    try {
      setCreditBalanceLoading(true);
      
      const { data: walletData, error: walletError } = await supabase
        .from('wallets')
        .select(`id, currency`)
        .eq('org_id', selectedCompany?.company_id)
        .maybeSingle();
  
      if (walletError || !walletData) {
        setCredit(0);
        setCurrencyBalance(0); // ✅ Reset currency balance
        setCreditBalanceLoading(false);
        return;
      }
  
      // ✅ Fetch both free_credit AND balance
      const { data: balanceData, error: balanceError } = await supabase
        .from('wallet_balances')
        .select('free_credit, balance') // ✅ Changed from just 'free_credit'
        .eq('wallet_id', walletData.id)
        .maybeSingle();
  
      if (balanceError) {
        setCredit(0);
        setCurrencyBalance(0); // ✅ Reset currency balance
        setCreditBalanceLoading(false);
        return;
      }
  
      setCredit(balanceData?.free_credit || 0);
      setCurrencyBalance(balanceData?.balance || 0); // ✅ Set currency balance
    } catch (error) {
      console.error('Error fetching credit balance:', error);
      setCredit(0);
      setCurrencyBalance(0); // ✅ Reset on error
    } finally {
      setCreditBalanceLoading(false);
    }
  }, [selectedCompany?.company_id]);

  useEffect(() => {
    if (companyId && selectedCompany?.company_id !== companyId) {
      const company = userCompanies.find((c) => c.company_id === companyId);
      if (company) setSelectedCompany(company);
    }
  }, [companyId, selectedCompany?.company_id, userCompanies, setSelectedCompany]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    
    if (paymentSuccess === 'true') {
      fetchCreditBalance();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [selectedCompany?.company_id, fetchCreditBalance]);

  // Fetch user's groups
  useEffect(() => {
    const fetchUserGroups = async () => {
      if (!selectedCompany || !user?.id) {
        setUserGroupIds([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id)
          .eq('org_id', selectedCompany.company_id);

        if (error) {
          console.error('Error fetching user groups:', error);
          return;
        }

        const groupIds = data?.map(gm => gm.group_id) || [];
        setUserGroupIds(groupIds);
      } catch (error) {
        console.error('Error fetching user groups:', error);
      }
    };

    fetchUserGroups();
  }, [selectedCompany?.company_id, user?.id]);

  useEffect(() => {
    if (selectedCompany) {
      fetchGroups();
    }
  }, [selectedCompany?.company_id, userGroupIds]);

  useEffect(() => {
    fetchCreditBalance();
  }, [selectedCompany?.company_id]);

  useEffect(() => {
    const fetchDocuments = async () => {
      if (!selectedCompany) {
        console.log('No company selected');
        setDocuments([]);
        setTotalFilingsCount(0);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Determine if we should fetch archived filings based on showArchived state
        const shouldFetchArchived = showArchived && permissions['file_archive'];
        
        // Build base query for fetching data
        const buildQuery = (withCount = false) => {
          let query = supabase
            .from('filings')
            .select(
              withCount 
                ? `id, company_info, status, created_at, updated_at, group_id, filing_types(name, code), processing_status, pinned_at, archived_at, created_by, updated_by`
                : `id, company_info, status, created_at, updated_at, group_id, filing_types(name, code), processing_status, pinned_at, archived_at, created_by, updated_by`,
              withCount ? { count: 'exact' } : undefined
            )
            .eq('org_id', selectedCompany.company_id)
            .is('deleted_at', null)
            .order('pinned_at', { ascending: false, nullsFirst: false })
            .order('created_at', { ascending: false });

          // Handle archived filter
          if (shouldFetchArchived) {
            query = query.eq('status', 'archived');
          } else {
            query = query.neq('status', 'archived');
          }

          // For non-admins, only show filings from their groups or unassigned filings
          if (!shouldFetchArchived) {
            if (!isUserAdmin && userGroupIds.length > 0) {
              query = query.or(`group_id.is.null,group_id.in.(${userGroupIds.join(',')})`);
            } else if (!isUserAdmin && userGroupIds.length === 0) {
              query = query.is('group_id', null);
            }
          }

          return query;
        };

        // Fetch data in batches of 1000 to bypass the limit
        const BATCH_SIZE = 1000;
        let allFilings: any[] = [];
        let hasMore = true;
        let offset = 0;

        while (hasMore) {
          const query = buildQuery(offset === 0); // Get count only on first batch
          const { data: batchFilings, error, count } = await query
            .range(offset, offset + BATCH_SIZE - 1);

          if (error) {
            console.error("Error fetching documents:", error);
            setErrorMsg("Failed to load filings. Please try again.");
            setDocuments([]);
            setTotalFilingsCount(0);
            return;
          }

          if (batchFilings && batchFilings.length > 0) {
            allFilings = [...allFilings, ...batchFilings];
            offset += BATCH_SIZE;
            
            // Check if we have more data to fetch
            hasMore = batchFilings.length === BATCH_SIZE;
            
            // Set count from first batch
            if (count !== null && count !== undefined) {
              setTotalFilingsCount(count);
            }
          } else {
            hasMore = false;
          }
        }

        console.log(`Fetched ${allFilings.length} filings in total`);

        // Fetch user profiles for all created_by and updated_by user IDs
        const createdByUserIds = Array.from(new Set(allFilings?.map(f => f.created_by).filter(Boolean) || []));
        const updatedByUserIds = Array.from(new Set(allFilings?.map(f => f.updated_by).filter(Boolean) || []));
        const allUserIds = Array.from(new Set([...createdByUserIds, ...updatedByUserIds]));
        let userProfilesMap: Record<string, string | null> = {};

        if (allUserIds.length > 0) {
          const { data: userProfiles, error: profilesError } = await supabase
            .from('user_profiles')
            .select('id, display_name')
            .in('id', allUserIds);

          if (!profilesError && userProfiles) {
            // Create a map for quick lookup: userId -> display_name
            userProfilesMap = userProfiles.reduce((acc, profile) => {
              acc[profile.id] = profile.display_name;
              return acc;
            }, {} as Record<string, string | null>);
          }
        }

        const transformedFilings: FilingDocument[] = allFilings?.map(filing => {
          // Parse company_info JSONB
          const companyInfo = typeof filing.company_info === 'string' 
            ? JSON.parse(filing.company_info) 
            : filing.company_info;
          
          // Extract company name
          // If company_info doesn't exist or is empty, show "Analysing file..."
          let companyName: string;
          if (!companyInfo || Object.keys(companyInfo).length === 0) {
            companyName = 'Analysing file...';
          } else {
            companyName = companyInfo.company_name || 'Unknown Company';
          }
          
          // Extract and format year end date from currentPeriod.end (format: YYYYMMDD)
          const yearEndRaw = companyInfo?.currentPeriod?.end || companyInfo?.reporting_period?.end || '';
          const yearEnd = yearEndRaw 
            ? `${yearEndRaw.substring(0, 4)}-${yearEndRaw.substring(4, 6)}-${yearEndRaw.substring(6, 8)}`
            : 'No Year End';

          // Get created_by and updated_by names from the map
          const createdByName = filing.created_by 
            ? userProfilesMap[filing.created_by] || null
            : null;
          
          const updatedByName = filing.updated_by 
            ? userProfilesMap[filing.updated_by] || null
            : null;

          const filingType = Array.isArray(filing.filing_types)
            ? filing.filing_types[0]
            : filing.filing_types;

          return {
            id: filing.id,
            company_name: companyName,
            filing_type_code: filingType?.code || null,
            filing_type_name: filingType?.name || null,
            year_end: yearEnd,
            status: filing.status,
            created_at: filing.created_at,
            updated_at: filing.updated_at,
            group_id: filing.group_id,
            processing_status: filing.processing_status,
            pinned_at: filing.pinned_at,
            created_by: filing.created_by, 
            created_by_name: createdByName,
            updated_by: filing.updated_by,
            updated_by_name: updatedByName
          };
        }) || [];

        console.log('Transformed filings:', transformedFilings.length);
        setDocuments(transformedFilings);
      } catch (error) {
        console.error("Error fetching documents:", error);
      } finally {
        setLoading(false);
        setIsRefreshing(false); // Reset manual refresh state
      }
    };
    const timeoutId = setTimeout(fetchDocuments, 200);
    return () => clearTimeout(timeoutId);
  }, [selectedCompany?.company_id, selectedGroupId, searchTerm, statusFilter, refreshTrigger, showArchived, permissions, isUserAdmin, userGroupIds]);

  useEffect(() => {
    // Auto-assign group when upload modal opens
    if (isUploadModalOpen) {
      if (userGroupIds.length === 1) {
        // User is only in one group - auto-assign to that group
        setUploadGroupId(userGroupIds[0]);
      } else if (userGroupIds.length === 0) {
        // User is not in any group - assign to default
        setUploadGroupId(null);
      }
      // If userGroupIds.length > 1, don't auto-assign (let user choose)
    }
  }, [isUploadModalOpen, userGroupIds]);

  useEffect(() => {
    if (selectedCompany?.company_id) {
      // Reset data immediately when company changes
      setMonthlySpend([]);
      setTotalConversions(0);
      fetchDashboardData();
      setReferralCode(selectedCompany.company_id);
    } else {
      // Reset when no company selected
      setMonthlySpend([]);
      setTotalConversions(0);
    }
  }, [selectedCompany?.company_id]);
  
  // Add this function after fetchGroups (around line 555)
  const fetchDashboardData = async () => {
    if (!selectedCompany?.company_id) {
      // Reset data if no company
      setMonthlySpend([]);
      setTotalConversions(0);
      setDashboardLoading(false);
      return;
    }
  
    try {
      setDashboardLoading(true);
  
      // Call RPC function to get monthly filing counts
      const { data: monthlyData, error } = await supabase
        .rpc('get_monthly_filings', { 
          p_org_id: selectedCompany.company_id
        });
  
      if (error) {
        console.error('Error fetching monthly filings:', error);
        setMonthlySpend([]);
        setTotalConversions(0);
      } else {
        // Convert RPC result to object for quick lookup
        const dataMap: { [key: string]: number } = {};
        let totalFilings = 0;
  
        (monthlyData || []).forEach((row: any) => {
          dataMap[row.month_key] = Number(row.filing_count);
          totalFilings += Number(row.filing_count);
        });
  
        // Generate last 12 months array
        const months: MonthlySpend[] = [];
        for (let i = 11; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          const monthName = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
          
          months.push({
            month: monthName,
            amount: dataMap[monthKey] || 0
          });
        }
  
        setMonthlySpend(months);
        setTotalConversions(totalFilings);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Reset data on error
      setMonthlySpend([]);
      setTotalConversions(0);
    } finally {
      setDashboardLoading(false);
    }
  };

  // Add this function after fetchDashboardData
  const copyReferralCode = () => {
    if (referralCode) {
      const referralLink = `${window.location.origin}/signup?ref=${referralCode}`;
      navigator.clipboard.writeText(referralLink).then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }).catch(() => {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = referralLink;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      });
    }
  };
  
  // Add these constants before the return statement (around line 1370)
  const maxAmount = Math.max(...monthlySpend.map(m => m.amount), 1);
  const chartHeight = 60;
  const chartWidth = 600;

  const filteredDocuments = useMemo(() => {
    let filtered = documents.filter(
      (doc) =>
        doc.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.year_end.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Filter by archive/active view first
    if (showArchived && permissions['file_archive']) {
      // Show only archived filings when Archive button is selected
      filtered = filtered.filter(doc => doc.status === 'archived');
    } else {
      // Exclude archived filings when Active button is selected (or no permission)
      filtered = filtered.filter(doc => doc.status !== 'archived');
    }

    // Filter by status (processing, converted, draft, validated, failed)
    if (statusFilter) {
      filtered = filtered.filter(doc => doc.status === statusFilter);
    }

    // Filter by created date range (inclusive day bounds)
    if (createdFrom) {
      const fromMs = dayjs(createdFrom).startOf('day').valueOf();
      filtered = filtered.filter(doc => dayjs(doc.created_at).valueOf() >= fromMs);
    }
    if (createdTo) {
      const toMs = dayjs(createdTo).endOf('day').valueOf();
      filtered = filtered.filter(doc => dayjs(doc.created_at).valueOf() <= toMs);
    }

    // Filter by selected group
    if (selectedGroupId !== null) {
      if (selectedGroupId === 'unassigned') {
        // Show only unassigned filings (group_id is null)
        filtered = filtered.filter(doc => !doc.group_id || doc.group_id === null);
      } else {
        // Show only filings in the selected group
        filtered = filtered.filter(doc => doc.group_id === selectedGroupId);
      }
    }

    // Sort: Pinned first (by pinned_at DESC), then unpinned (by created_at DESC)
    return filtered.sort((a, b) => {
      // Both pinned: sort by pinned_at DESC (latest pin first)
      if (a.pinned_at && b.pinned_at) {
        return new Date(b.pinned_at).getTime() - new Date(a.pinned_at).getTime();
      }
      // Only a pinned: a comes first
      if (a.pinned_at) return -1;
      // Only b pinned: b comes first
      if (b.pinned_at) return 1;
      // Neither pinned: sort by created_at DESC
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [documents, searchTerm, statusFilter, createdFrom, createdTo, selectedGroupId, showArchived, permissions]);

  const hasAdvancedFilters =
    statusFilter !== null ||
    createdFrom !== '' ||
    createdTo !== '';

  const hasActiveFilters =
    searchTerm.trim() !== '' ||
    hasAdvancedFilters;

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter(null);
    setCreatedFrom('');
    setCreatedTo('');
    setDraftStatusFilter(null);
    setDraftCreatedFrom('');
    setDraftCreatedTo('');
    setShowFilterPanel(false);
    setCurrentPage(1);
    setSelectedFilings(new Set());
  };

  const openFilterPanel = () => {
    setDraftStatusFilter(statusFilter);
    setDraftCreatedFrom(createdFrom);
    setDraftCreatedTo(createdTo);
    setShowFilterPanel((v) => !v);
  };

  const applyAdvancedFilters = () => {
    if (draftCreatedFrom && draftCreatedTo && draftCreatedFrom > draftCreatedTo) {
      alert('From date must be earlier than or equal to To date.');
      return;
    }
    setStatusFilter(draftStatusFilter);
    setCreatedFrom(draftCreatedFrom);
    setCreatedTo(draftCreatedTo);
    setCurrentPage(1);
    setSelectedFilings(new Set());
    setShowFilterPanel(false);
  };

  useEffect(() => {
    if (!showFilterPanel) return;
    const onPointerDown = (e: MouseEvent) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target as Node)) {
        setShowFilterPanel(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowFilterPanel(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showFilterPanel]);

  const handleCreditBalance = () => {
    setIsPurchaseModalOpen(true);
    fetchProducts();
  };

  const fetchGroups = async () => {
    if (!selectedCompany) {
      setGroups([]);
      setGroupsLoading(false);
      return;
    }
    
    try {
      setGroupsLoading(true);
      
      const { data, error } = await supabase
        .from('organization_groups')
        .select('id, name, description')
        .eq('org_id', selectedCompany.company_id)
        .order('name');
      
      if (error) {
        console.error('Error fetching groups:', error);
        setGroupsLoading(false);
        return;
      }
      
      // For non-admins, only show groups they belong to
      let filteredGroups = data || [];
      if (!isUserAdmin && userGroupIds.length > 0) {
        filteredGroups = filteredGroups.filter(g => userGroupIds.includes(g.id));
      } else if (!isUserAdmin) {
        setGroups([]);
        setGroupsLoading(false);
        return;
      }
      
      // Count members per group from group_members table
      const groupsWithCounts = await Promise.all(
        filteredGroups.map(async (group) => {
          const { count } = await supabase
            .from('group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id)
            .eq('org_id', selectedCompany.company_id);
          
          return {
            ...group,
            member_count: count || 0
          };
        })
      );
      
      setGroups(groupsWithCounts);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setGroupsLoading(false);
    }
  };

  const fetchProducts = async () => {
    if (!selectedCompany?.company_id) {
      console.log('No company selected');
      return;
    }

    setLoadingProducts(true);
    try {
      const { data: orgData } = await supabase
        .from('organizations')
        .select('country_iso')
        .eq('id', selectedCompany.company_id)
        .single();

      if (!orgData) {
        setLoadingProducts(false);
        return;
      }

      const [productsResult, purchasesResult] = await Promise.all([
        supabase
          .from('products')
          .select('*')
          .eq('country_iso', orgData?.country_iso)
          .eq('is_active', true)
          .order('price', { ascending: true }),
        supabase
          .from('org_purchases')
          .select('product_id')
          .eq('org_id', selectedCompany.company_id)
          .eq('status', 'paid'),
      ]);

      if (productsResult.error) {
        console.log('Error fetching products:', productsResult.error);
        setLoadingProducts(false);
        return;
      }

      setProducts(productsResult.data || []);

      const paidIds = new Set((purchasesResult.data || []).map((p: { product_id: string }) => p.product_id));
      setPurchasedProductIds(paidIds);

      setLoadingProducts(false);
    } catch {
      setLoadingProducts(false);
    }
  };

  const handleFileUpload = () => {
    setIsUploadModalOpen(true);
    fetchFilingTypes();
    // Reset form
    setUploadFiles([]);
    setUploadGroupId(null);
    setUploadFilingTypeId('');
  };

  const fetchFilingTypes = async () => {
    if (!selectedCompany) return;
    
    try {
      const { data: orgData } = await supabase
        .from('organizations')
        .select('country_iso')
        .eq('id', selectedCompany.company_id)
        .single();

      if (!orgData) return;

      const { data, error } = await supabase
        .from('filing_types')
        .select('id, name, code, currency_cost')
        .eq('country_iso', orgData.country_iso)
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Error fetching filing types:', error);
        return;
      }

      setFilingTypes(data || []);
    } catch (error) {
      console.error('Error fetching filing types:', error);
    }
  };

  const getPdfPageCount = async (file: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          console.log('📄 Reading PDF, file size:', arrayBuffer.byteLength);
          const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
          const pdf = await loadingTask.promise;
          const pageCount = pdf.numPages;
          console.log('📄 PDF page count:', pageCount);
          resolve(pageCount);
        } catch (error) {
          console.error('Error reading PDF:', error);
          reject(error);
        }
      };
      fileReader.onerror = (error) => {
        console.error('FileReader error:', error);
        reject(error);
      };
      fileReader.readAsArrayBuffer(file);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0]; // Only take the first file
      
      console.log('📁 File selected:', file.name, file.size);
      
      // Validate it's a PDF
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        setErrorMsg('Only PDF files are allowed');
        setUploadFiles([]);
        // Reset the input
        e.target.value = '';
        return;
      }

      // Check PDF page count (with fallback for older browsers like Windows 8)
      try {
        console.log('🔍 Starting PDF page count check...');
        setCheckingPdfPages(true);
        setErrorMsg(null);
        const pageCount = await getPdfPageCount(file);
        console.log('✅ Page count received:', pageCount);
        
        if (pageCount < 5) {
          console.log('⚠️ PDF has less than 5 pages, showing modal');
          // Show confirmation modal for PDFs with less than 5 pages
          setPendingPdfFile({ file, pageCount });
          setShowPdfPageCountModal(true);
          console.log('✅ Modal state set:', { showPdfPageCountModal: true, pendingPdfFile: { file: file.name, pageCount } });
          // Reset the input
          e.target.value = '';
          setCheckingPdfPages(false);
          return;
        }
        
        console.log('✅ PDF has 5+ pages, proceeding normally');
        // PDF has 5 or more pages, proceed normally
        setUploadFiles([file]);
        setErrorMsg(null);
        
        // Auto-assign group based on user's group membership
        if (userGroupIds.length === 1) {
          setUploadGroupId(userGroupIds[0]);
        } else if (userGroupIds.length === 0) {
          setUploadGroupId(null);
        }
      } catch (error) {
        // 🔑 FALLBACK: If PDF.js fails (e.g., older browsers on Windows 8),
        // skip the page count check and allow upload anyway
        console.warn('⚠️ PDF page check failed (browser may not support PDF.js), allowing upload anyway:', error);
        setUploadFiles([file]);
        setErrorMsg(null);
        
        // Auto-assign group based on user's group membership
        if (userGroupIds.length === 1) {
          setUploadGroupId(userGroupIds[0]);
        } else if (userGroupIds.length === 0) {
          setUploadGroupId(null);
        }
      } finally {
        setCheckingPdfPages(false);
      }
    }
  };

  const handleConfirmPdfUpload = () => {
    if (pendingPdfFile) {
      setUploadFiles([pendingPdfFile.file]);
      setShowPdfPageCountModal(false);
      setPendingPdfFile(null);
      
      // Auto-assign group based on user's group membership
      if (userGroupIds.length === 1) {
        setUploadGroupId(userGroupIds[0]);
      } else if (userGroupIds.length === 0) {
        setUploadGroupId(null);
      }
    }
  };

  const handleCancelPdfUpload = () => {
    setShowPdfPageCountModal(false);
    setPendingPdfFile(null);
    setUploadFiles([]);
  };

  const handleUploadSubmit = async () => {
    if (!selectedCompany || uploadFiles.length === 0) {
      setErrorMsg('Please select a PDF file');
      return;
    }
  
    // Validate that we only have one file and it's a PDF
    if (uploadFiles.length > 1) {
      setErrorMsg('Please select only one file');
      return;
    }
  
    const file = uploadFiles[0];
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setErrorMsg('Only PDF files are allowed');
      return;
    }
  
    // ✅ Validate filing type is selected (required for SG)
    if (!uploadFilingTypeId) {
      setErrorMsg('Please select a filing type');
      return;
    }
  
    // Validate group assignment for non-admins
    if (!isUserAdmin && uploadGroupId !== null) {
      if (!userGroupIds.includes(uploadGroupId)) {
        setErrorMsg('You can only assign filings to groups you belong to');
        return;
      }
    }
  
    // ✅ Calculate deductions before showing modal
    try {
      // Get current balances
      const { data: walletData } = await supabase
        .from('wallets')
        .select('id')
        .eq('org_id', selectedCompany.company_id)
        .maybeSingle();
  
      if (!walletData) {
        setErrorMsg('Wallet not found');
        return;
      }
  
      const { data: balanceData } = await supabase
        .from('wallet_balances')
        .select('free_credit, balance')
        .eq('wallet_id', walletData.id)
        .maybeSingle();
  
      const currentFreeCredit = balanceData?.free_credit || 0;
      const currentCurrencyBalance = balanceData?.balance || 0;
  
      // ✅ Fetch filing type cost
      const { data: filingTypeData } = await supabase
        .from('filing_types')
        .select('currency_cost')
        .eq('id', uploadFilingTypeId)
        .single();
  
      const filingTypeCost = filingTypeData?.currency_cost || 0;
  
      // ✅ Check if user has sufficient balance
      if (currentFreeCredit === 0 && currentCurrencyBalance < filingTypeCost) {
        setInsufficientInfo({
          currentFreeCredit,
          currentCurrencyBalance,
          requiredCurrencyBalance: filingTypeCost,
        });
        setShowInsufficientBalanceModal(true);
        return;
      }
  
      // ✅ Calculate deductions
      let freeCreditDeduction = 0;
      let currencyBalanceDeduction = 0;
  
      if (currentFreeCredit > 0) {
        // Deduct 1 free credit first
        freeCreditDeduction = 1;
        currencyBalanceDeduction = 0;
      } else {
        // No free credit - use currency balance
        freeCreditDeduction = 0;
        currencyBalanceDeduction = filingTypeCost;
      }
  
      // ✅ Store deductions in state for modal
      setPendingDeductions({
        freeCredit: freeCreditDeduction,
        currencyBalance: currencyBalanceDeduction
      });
  
      // Store upload data and show confirmation modal
      setPendingUpload({
        file,
        filingTypeId: uploadFilingTypeId,
        groupId: uploadGroupId
      });
      setShowCreditDeductionModal(true);
    } catch (error: any) {
      console.error('Error calculating deductions:', error);
      setErrorMsg('Failed to calculate deductions. Please try again.');
    }
  };

  const handleConfirmedUpload = async () => {
    if (!selectedCompany || !pendingUpload || !user) {
      setErrorMsg('Missing required information');
      setShowCreditDeductionModal(false);
      return;
    }

    try {
      setUploading(true);
      setErrorMsg(null);
      setShowCreditDeductionModal(false);

      // Resolve session (with refresh + retry)
      let session = null;
      let retries = 0;
      const maxRetries = 2;

      while (retries <= maxRetries && !session) {
        try {
          const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
          if (sessionError) throw sessionError;

          if (sessionData.session) {
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

            if (refreshError) {
              const isAuthError =
                refreshError.message?.includes('expired') ||
                refreshError.message?.includes('invalid') ||
                refreshError.message?.includes('JWT') ||
                refreshError.message?.includes('refresh_token') ||
                refreshError.status === 401 ||
                refreshError.status === 400;

              if (isAuthError) {
                setErrorMsg('Your session has expired. Please sign in again.');
                alert('Your session has expired. Please sign in again.');
                setTimeout(() => { window.location.href = '/signin'; }, 2000);
                return;
              }
            }

            session = (!refreshError && refreshData.session) ? refreshData.session : sessionData.session;
          }
        } catch (err: any) {
          if (err.message?.includes('expired') || err.message?.includes('invalid') || err.message?.includes('JWT')) {
            setErrorMsg('Your session has expired. Please sign in again.');
            alert('Your session has expired. Please sign in again.');
            setTimeout(() => { window.location.href = '/signin'; }, 2000);
            return;
          }
          retries++;
          if (retries > maxRetries) throw err;
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      if (!session) {
        setErrorMsg('No active session. Please sign in again.');
        alert('No active session. Please sign in again.');
        setTimeout(() => { window.location.href = '/signin'; }, 2000);
        return;
      }

      // v5: v3 flow + do not treat a lost complete reply as fail
      console.log(`📝 Original filename: ${pendingUpload.file.name}`);
      console.log(`✅ Sanitized filename: ${sanitizeUploadFilename(pendingUpload.file.name)}`);

      let payload;
      try {
        payload = await uploadFilingPdfV5({
          accessToken: session.access_token,
          file: pendingUpload.file,
          orgId: selectedCompany.company_id,
          filingTypeId: pendingUpload.filingTypeId,
          groupId: pendingUpload.groupId,
        });
      } catch (uploadErr: any) {
        if (uploadErr?.code === 'INSUFFICIENT_BALANCE' || uploadErr?.message === 'INSUFFICIENT_BALANCE') {
          setInsufficientInfo({
            currentFreeCredit: uploadErr.free_credit ?? 0,
            currentCurrencyBalance: uploadErr.currency_balance ?? 0,
            requiredCurrencyBalance: uploadErr.required ?? 0,
          });
          setShowInsufficientBalanceModal(true);
          return;
        }
        const msg = String(uploadErr?.message || '');
        if (
          msg.includes('401') ||
          msg.toLowerCase().includes('unauthorized') ||
          msg.toLowerCase().includes('expired') ||
          msg.includes('JWT')
        ) {
          setErrorMsg('Your session has expired. Please sign in again.');
          alert('Your session has expired. Please sign in again.');
          setTimeout(() => { window.location.href = '/signin'; }, 2000);
          return;
        }
        throw uploadErr;
      }

      console.log('Filing created by server (v5):', payload.filing_id);

      if (payload.billing_warning) {
        console.warn('Billing warning:', payload.billing_warning);
      }

      if (payload.credits_remaining != null) setCredit(payload.credits_remaining);
      if (payload.balance_remaining != null) setCurrencyBalance(payload.balance_remaining);

      setSuccessMsg('Filing created successfully');
      setIsUploadModalOpen(false);
      setRefreshTrigger(prev => prev + 1);
      fetchCreditBalance();

      // Reset form
      setUploadFiles([]);
      setUploadGroupId(null);
      setUploadFilingTypeId('');
      setPendingUpload(null);
      setPendingDeductions({ freeCredit: 0, currencyBalance: 0 });
    } catch (error: any) {
      console.error('Upload error:', error);
      setErrorMsg(error.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };
  
  const fetchAllGroups = async () => {
    if (!selectedCompany) {
      setAllGroups([]);
      setAllGroupsLoading(false);
      return;
    }
    
    try {
      setAllGroupsLoading(true);
      
      const { data, error } = await supabase
        .from('organization_groups')
        .select('id, name, description')
        .eq('org_id', selectedCompany.company_id)
        .order('name');
      
      if (error) {
        console.error('Error fetching all groups:', error);
        setAllGroups([]);
      } else {
        setAllGroups(data || []);
      }
    } catch (error) {
      console.error('Error fetching all groups:', error);
      setAllGroups([]);
    } finally {
      setAllGroupsLoading(false);
    }
  };

  const totalPages = Math.ceil(filteredDocuments.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentDocuments = filteredDocuments.slice(startIndex, endIndex);

  const handleDeleteFiling = async (filingId: string) => {
    try {
      const { error } = await supabase
        .from('filings')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', filingId);

      if (error) {
        console.error('Error deleting filing:', error);
        alert('Failed to delete filing. Please try again.');
        return;
      }

      setDocuments(prev => prev.filter(doc => doc.id !== filingId));
      console.log('Filing deleted successfully');
    } catch (error) {
      console.error('Error deleting filing:', error);
      alert('Failed to delete filing. Please try again.');
    }
  };

  const handleDownloadFiling = async (doc: FilingDocument) => {
    try {
      // Show loading state
      setDownloadingFilingId(doc.id);
      console.log(`Generating XBRL for filing: ${doc.id}`);
      
      // Call Lambda function to generate and download XBRL
      await downloadXBRL(doc.id, 'SG');
      
      // Success notification
      setSuccessMsg('XBRL file downloaded successfully!');
      setTimeout(() => setSuccessMsg(null), 3000);
      
    } catch (error: any) {
      console.error('XBRL download error:', error);
      
      // Error notification
      setErrorMsg(error.message || 'Failed to generate XBRL file. Please try again.');
      setTimeout(() => setErrorMsg(null), 5000);
    } finally {
      setDownloadingFilingId(null);
    }
  };

  const handleDownloadTipConfirm = async () => {
    const docToDownload = pendingDownloadDoc;
    setShowDownloadTipModal(false);
    setPendingDownloadDoc(null);
    if (docToDownload) {
      await handleDownloadFiling(docToDownload);
    }
  };

  const handleArchiveFiling = async () => {
    if (selectedFilings.size === 0) {
      setErrorMsg('Please select at least one filing to archive.');
      setTimeout(() => setErrorMsg(null), 3000);
      return;
    }

      setShowArchiveConfirm(true);
    }

  const confirmArchiveFiling = async () => {
    try {
      const filingIds = Array.from(selectedFilings);
      const { error } = await supabase
        .from('filings')
        .update({ archived_at: new Date().toISOString(),
          status: 'archived'
         })
        .in('id', filingIds);

      if (error) {
        console.error('Error archiving filings:', error);
        setErrorMsg('Failed to archive filings. Please try again.');
        setTimeout(() => setErrorMsg(null), 3000);
        return;
      }

      // ✅ Close modal FIRST before clearing selections
      setShowArchiveConfirm(false);
    
      // Remove archived filings from the current view
      setDocuments(prev => prev.map(doc => 
        selectedFilings.has(doc.id) 
          ? { ...doc, status: 'archived', archived_at: new Date().toISOString() }
          : doc
      ));
      setSelectedFilings(new Set());
      setSuccessMsg(`${filingIds.length} filing(s) archived successfully.`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (error) {
      console.error('Error archiving filings:', error);
      setErrorMsg('Failed to archive filings. Please try again.');
      setTimeout(() => setErrorMsg(null), 3000);
    }
  };

  const handleUnarchiveFiling = async () => {
    if (selectedFilings.size === 0) {
      setErrorMsg('Please select at least one filing to unarchive.');
      setTimeout(() => setErrorMsg(null), 3000);
      return;
    }
    setShowUnarchiveConfirm(true);
  };
  
  const confirmUnarchiveFiling = async () => {
    try {
      const filingIds = Array.from(selectedFilings);
      const { error } = await supabase
        .from('filings')
        .update({ 
          archived_at: null,
          status: 'draft' // Restore to draft status
         })
        .in('id', filingIds);
  
      if (error) {
        console.error('Error unarchiving filings:', error);
        setErrorMsg('Failed to unarchive filings. Please try again.');
        setTimeout(() => setErrorMsg(null), 3000);
        return;
      }
  
      // Close modal first
      setShowUnarchiveConfirm(false);
      
      // Update documents state
      setDocuments(prev => prev.map(doc => 
        selectedFilings.has(doc.id) 
          ? { ...doc, status: 'draft', archived_at: null }
          : doc
      ));
      
      // Clear selections
      setSelectedFilings(new Set());
      
      // Trigger refresh
      // setRefreshTrigger(prev => prev + 1);
      
      setSuccessMsg(`${filingIds.length} filing(s) unarchived successfully.`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (error) {
      console.error('Error unarchiving filings:', error);
      setErrorMsg('Failed to unarchive filings. Please try again.');
      setTimeout(() => setErrorMsg(null), 3000);
    }
  };

  // Handle checkbox selection
  const handleSelectFiling = (filingId: string) => {
    setSelectedFilings(prev => {
      const newSet = new Set(prev);
      if (newSet.has(filingId)) {
        newSet.delete(filingId);
      } else {
        newSet.add(filingId);
      }
      return newSet;
    });
  };
  
  const handleSelectAll = () => {
    if (selectedFilings.size === currentDocuments.length) {
      setSelectedFilings(new Set());
    } else {
      setSelectedFilings(new Set(currentDocuments.map(doc => doc.id)));
    }
  };

  const handleSaveGroupAssignments = async (filingId: string) => {
    try {
      const filing = documents.find(f => f.id === filingId);
      if (!filing) return;

      const currentGroupId = filing.group_id;
      const newGroupId = tempSelectedGroups.length > 0 ? tempSelectedGroups[0] : null;

      // Only update if changed
      if (currentGroupId !== newGroupId) {
        const { error } = await supabase
          .from('filings')
          .update({ group_id: newGroupId })
          .eq('id', filingId);

        if (error) throw error;

        // Update local state immediately
        setDocuments(prev => prev.map(doc => 
          doc.id === filingId ? { ...doc, group_id: newGroupId } : doc
        ));

        setShowGroupAssignmentModal(null);
        setTempSelectedGroups([]);
        setSuccessMsg("Filing group assignment updated successfully");
        setTimeout(() => {
          setSuccessMsg(null);
        }, 2000);
      } else {
        setShowGroupAssignmentModal(null);
        setTempSelectedGroups([]);
      }
    } catch (error) {
      console.error('Error saving group assignments:', error);
      setErrorMsg('Failed to update group assignment');
      setTimeout(() => {
        setErrorMsg(null);
      }, 3000);
    }
  };
  
  const handleTogglePin = async (filingId: string, currentPinnedAt: string | null) => {
    try {
      const newPinnedAt = currentPinnedAt ? null : new Date().toISOString();
      
      const { error } = await supabase
        .from('filings')
        .update({ pinned_at: newPinnedAt })
        .eq('id', filingId);
  
      if (error) throw error;
  
      // Update local state
      setDocuments(prev => prev.map(doc => 
        doc.id === filingId 
          ? { ...doc, pinned_at: newPinnedAt }
          : doc
      ));
  
      setSuccessMsg(newPinnedAt ? 'Filing pinned' : 'Filing unpinned');
      setTimeout(() => setSuccessMsg(null), 2000);
    } catch (error) {
      console.error('Error toggling pin:', error);
      setErrorMsg('Failed to toggle pin');
      setTimeout(() => setErrorMsg(null), 3000);
    }
  };

  // Supabase Realtime - Live status updates for processing filings ONLY
  // Track if there are processing filings (for realtime subscription)
  const hasProcessingFilings = documents.some(doc => doc.status === 'processing');

  // IMPORTANT: Only depend on company_id and hasProcessingFilings (boolean), NOT documents array
  // Otherwise every realtime update causes re-subscribe loop
  useEffect(() => {
    if (!selectedCompany?.company_id) return;

    if (!hasProcessingFilings) {
      console.log('⏸️ No processing filings - skipping Realtime subscription');
      return;
    }

    console.log('🔴 Setting up Realtime subscription for processing filings...');

    const channel = supabase
      .channel('filings-realtime')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'filings',
          filter: `org_id=eq.${selectedCompany.company_id}`
        },
        (payload) => {
          console.log('🔴 Realtime update received:', payload.new);

          const updatedFiling = payload.new as any;

          setDocuments(prev => {
            const index = prev.findIndex(d => d.id === updatedFiling.id);
            if (index === -1) return prev;

            if (updatedFiling.deleted_at) {
              return prev.filter(d => d.id !== updatedFiling.id);
            }

            const companyInfo = typeof updatedFiling.company_info === 'string'
              ? JSON.parse(updatedFiling.company_info)
              : updatedFiling.company_info;

            let companyName: string;
            if (!companyInfo || Object.keys(companyInfo).length === 0) {
              companyName = 'Analysing file...';
            } else {
              companyName = companyInfo.company_name || 'Unknown Company';
            }
            const yearEndRaw = companyInfo?.currentPeriod?.end || companyInfo?.reporting_period?.end || '';
            const yearEnd = yearEndRaw
              ? `${yearEndRaw.substring(0, 4)}-${yearEndRaw.substring(4, 6)}-${yearEndRaw.substring(6, 8)}`
              : 'No Year End';

            const updated: FilingDocument = {
              id: updatedFiling.id,
              company_name: companyName,
              filing_type_code: prev[index]?.filing_type_code || null,
              filing_type_name: prev[index]?.filing_type_name || null,
              year_end: yearEnd,
              status: updatedFiling.status,
              created_at: updatedFiling.created_at,
              created_by: updatedFiling.created_by,
              created_by_name: prev[index]?.created_by_name,
              updated_at: updatedFiling.updated_at,
              updated_by: updatedFiling.updated_by,
              updated_by_name: prev[index]?.updated_by_name,
              group_id: updatedFiling.group_id,
              processing_status: updatedFiling.processing_status,
              pinned_at: updatedFiling.pinned_at,
            };

            const newDocs = [...prev];
            newDocs[index] = updated;
            return newDocs;
          });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ Realtime subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Realtime subscription error');
        } else if (status === 'TIMED_OUT') {
          console.warn('⚠️ Realtime subscription timed out - will retry');
        }
      });

    return () => {
      console.log('🔴 Cleaning up Realtime subscription');
      channel.unsubscribe();
    };
  }, [selectedCompany?.company_id, hasProcessingFilings]);

  useEffect(() => {
    if (openActionMenu) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        // Don't close if clicking inside the dropdown or button
        if (!target.closest('.action-menu-dropdown') && !target.closest('.action-menu-button')) {
          setOpenActionMenu(null);
        }
      };
      
      window.addEventListener('click', handleClickOutside);
      return () => window.removeEventListener('click', handleClickOutside);
    }
  }, [openActionMenu]);
  
  const PurchaseCreditModal = () => {
    const [selectedProduct, setSelectedProduct] = useState<string | null>(null);

    if (!isPurchaseModalOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
        <div className="bg-white rounded-lg w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-blue-600">Purchase Credits</h3>
            <button
              onClick={() => setIsPurchaseModalOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
            {loadingProducts ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="text-gray-500 mt-2">Loading products...</p>
              </div>
            ) : products.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No products available</p>
            ) : (
              products.map((product, index) => {
                const alreadyPurchased = product.one_time_purchase && purchasedProductIds.has(product.id);
                const isDisabled = alreadyPurchased;
                const isOneTime = product.one_time_purchase;

                if (isOneTime) {
                  return (
                    <label
                      key={product.id}
                      className={`relative block border-2 rounded-lg overflow-hidden transition ${
                        isDisabled
                          ? 'border-gray-300 opacity-50 cursor-not-allowed'
                          : selectedProduct === product.id
                            ? 'border-red-500 ring-2 ring-red-200 cursor-pointer'
                            : 'border-red-300 hover:border-red-400 cursor-pointer'
                      }`}
                    >
                      {!isDisabled && (
                        <div className="bg-gradient-to-r from-red-500 to-pink-500 px-3 py-1.5 flex items-center justify-between">
                          <span className="text-white text-xs font-bold uppercase tracking-wide">{product.promotional_description || 'SPECIAL OFFER'}</span>
                          <span className="bg-white text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-full">ONE TIME PURCHASE ONLY</span>
                        </div>
                      )}
                      {isDisabled && (
                        <div className="bg-gray-200 px-3 py-1.5 text-center">
                          <span className="text-gray-500 text-xs font-semibold">Already Purchased</span>
                        </div>
                      )}
                      <div className="flex items-start p-3">
                        <input
                          type="radio"
                          name="product"
                          value={product.id}
                          checked={selectedProduct === product.id}
                          onChange={(e) => setSelectedProduct(e.target.value)}
                          className="mt-1 mr-3"
                          disabled={isDisabled}
                        />
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{product.name || `Package ${index + 1}`}</div>
                          <div className="text-sm font-bold text-red-600 mt-1">
                            {product.currency} {product.price.toFixed(2)}
                          </div>
                          <div className="text-sm text-gray-700 mt-1 font-semibold">
                            GET: {product.credit_amount} Credits
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                }

                return (
                  <label
                    key={product.id}
                    className={`flex items-start border-2 rounded-lg p-3 transition ${
                      selectedProduct === product.id
                        ? 'border-blue-500 bg-blue-50 cursor-pointer'
                        : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                    }`}
                  >
                    <input
                      type="radio"
                      name="product"
                      value={product.id}
                      checked={selectedProduct === product.id}
                      onChange={(e) => setSelectedProduct(e.target.value)}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">Package {index + 1}</div>
                      <div className="text-sm text-gray-700 mt-1">
                        {product.currency} {product.price.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-700 mt-1 font-semibold">
                        GET: {product.credit_amount} Credits
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-end space-x-3 p-4 border-t">
            <button
              onClick={() => setIsPurchaseModalOpen(false)}
              className="f-btn f-btn-soft"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (selectedProduct && selectedCompany?.company_id) {
                  setIsPurchaseModalOpen(false);
                  setIsPaymentLoading(true);
                  
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    
                    if (!session) {
                      setIsPaymentLoading(false);
                      alert('You must be logged in to make a purchase');
                      return;
                    }

                    const { data, error: functionError } = await supabase.functions.invoke('create-hitpay-payment', {
                      body: {
                        product_id: selectedProduct,
                        org_id: selectedCompany.company_id,
                      }
                    });
                    
                    if (functionError) {
                      console.error('Function error:', functionError);
                      setIsPaymentLoading(false);
                      let errorMsg = 'Failed to create payment. Please try again.';
                      try {
                        const errBody = await functionError.context?.json?.();
                        if (errBody?.error) errorMsg = errBody.error;
                      } catch { /* use default */ }
                      alert(errorMsg);
                      return;
                    }
                    
                    if (!data || data.error) {
                      setIsPaymentLoading(false);
                      alert(data?.error || 'Failed to create payment');
                      return;
                    }
                    
                    const checkoutUrl = data.checkout_url;
                    if (checkoutUrl) {
                      window.location.href = checkoutUrl;
                    } else {
                      setIsPaymentLoading(false);
                      alert('No checkout URL received');
                    }

                  } catch (err) {
                    console.error('Payment error:', err);
                    setIsPaymentLoading(false);
                    alert('Failed to initialize payment');
                  }
                }
              }}
              
              disabled={!selectedProduct}
              className="f-btn f-btn-primary"
            >
              Purchase
            </button>          
          </div>
        </div>
      </div>
    )
  }

  const PaymentLoadingModal = () => {
    if (!isPaymentLoading) return null;
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[10000]">
        <div className="bg-white rounded-lg p-8 max-w-sm w-full mx-4">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Redirecting to Payment...</h3>
            <p className="text-sm text-gray-600">Please wait while we prepare your secure checkout.</p>
            <p className="text-xs text-gray-500 mt-4">This may take a few seconds</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
    <div className="page-container">
      <div className="max-w-6xl mx-auto space-y-6">
        <AnnouncementsBanner placement="all_filing" />
        <SgXBRLPromoBanner />
        <SgOnboardingTour />
        
        {/* Dashboard Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Total Conversions Card */}
          <div className="lg:col-span-2 company-profile-card" style={{ backgroundColor: '#0d9488', color: 'white', padding: '0.5rem 0.75rem 0.5rem 0.75rem' }}>
            <div>
              <h2 className="text-base font-bold" style={{ color: 'white', marginTop: '0', marginBottom: '0.25rem' }}>Monthly Filings</h2>
            </div>

            {/* Chart */}
            <div className="mt-1" style={{ marginBottom: '0' }}>
              {dashboardLoading ? (
                <div className="flex items-center justify-center h-[100px]">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                </div>
              ) : (
                <div className="relative" style={{ height: `${chartHeight}px` }} ref={chartContainerRef}>
                  <svg width="100%" height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="xMidYMid meet">
                    {/* Grid lines */}
                    {[0, 0.5, 1].map((ratio) => (
                      <line
                        key={ratio}
                        x1="40"
                        y1={chartHeight - 15 - (chartHeight - 30) * ratio}
                        x2={chartWidth - 20}
                        y2={chartHeight - 15 - (chartHeight - 30) * ratio}
                        stroke="rgba(255, 255, 255, 0.2)"
                        strokeWidth="1"
                        strokeDasharray="4 4"
                      />
                    ))}

                    {/* Visible line chart - smooth curve */}
                    <polyline
                      fill="none"
                      stroke="rgba(255, 255, 255, 0.95)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      points={monthlySpend.map((data, index) => {
                        const x = 40 + (index * (chartWidth - 60) / (monthlySpend.length - 1 || 1));
                        const y = chartHeight - 15 - ((data.amount / maxAmount) * (chartHeight - 30));
                        return `${x},${y}`;
                      }).join(' ')}
                      style={{ pointerEvents: 'none' }}
                    />

                    {/* Clickable invisible paths along the line for each segment */}
                    {monthlySpend.map((data, index) => {
                      if (index === 0) return null; // Skip first point (no segment before it)
                      
                      const x1 = 40 + ((index - 1) * (chartWidth - 60) / (monthlySpend.length - 1 || 1));
                      const y1 = chartHeight - 15 - ((monthlySpend[index - 1].amount / maxAmount) * (chartHeight - 30));
                      const x2 = 40 + (index * (chartWidth - 60) / (monthlySpend.length - 1 || 1));
                      const y2 = chartHeight - 15 - ((data.amount / maxAmount) * (chartHeight - 30));
                      
                      // Helper function to calculate tooltip position
                      const calculateTooltipPosition = (pointX: number, pointY: number, svgElement: SVGSVGElement, containerRect: DOMRect) => {
                        const svgRect = svgElement.getBoundingClientRect();
                        const svgViewBox = svgElement.viewBox.baseVal;
                        
                        // Calculate scale factors based on actual rendered size vs viewBox
                        const scaleX = svgRect.width / svgViewBox.width;
                        const scaleY = svgRect.height / svgViewBox.height;
                        
                        // Convert viewBox coordinates to actual pixel position within SVG
                        const actualX = (pointX / svgViewBox.width) * svgRect.width;
                        const actualY = (pointY / svgViewBox.height) * svgRect.height;
                        
                        // Position relative to container (accounting for SVG position within container)
                        const containerX = actualX + (svgRect.left - containerRect.left);
                        const containerY = actualY + (svgRect.top - containerRect.top);
                        
                        return { x: containerX, y: containerY };
                      };
                      
                      return (
                        <line
                          key={`segment-${index}`}
                          x1={x1}
                          y1={y1}
                          x2={x2}
                          y2={y2}
                          stroke="transparent"
                          strokeWidth="20"
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => {
                            if (chartContainerRef.current) {
                              const containerRect = chartContainerRef.current.getBoundingClientRect();
                              const svgElement = e.currentTarget.closest('svg') as SVGSVGElement | null;
                              if (!svgElement) return;
                              
                              const svgRect = svgElement.getBoundingClientRect();
                              
                              // Get the actual point coordinates in viewBox
                              const pointX = x2;
                              const pointY = y2;
                              
                              // Calculate the position in the actual rendered SVG
                              const svgViewBox = svgElement.viewBox.baseVal;
                              const scaleX = svgRect.width / svgViewBox.width;
                              const scaleY = svgRect.height / svgViewBox.height;
                              
                              // Convert viewBox coordinates to pixel position within SVG
                              const pixelX = (pointX / svgViewBox.width) * svgRect.width;
                              const pixelY = (pointY / svgViewBox.height) * svgRect.height;
                              
                              // Convert to container-relative position
                              const containerX = pixelX + (svgRect.left - containerRect.left);
                              const containerY = pixelY + (svgRect.top - containerRect.top);
                              
                              setHoveredMonthIndex(index);
                              setTooltipPosition({
                                x: containerX,
                                y: containerY 
                              });
                            }
                          }}
                          onMouseLeave={() => {
                            setHoveredMonthIndex(null);
                            setTooltipPosition(null);
                          }}
                          onTouchStart={(e) => {
                            if (chartContainerRef.current) {
                              const containerRect = chartContainerRef.current.getBoundingClientRect();
                              const svgElement = e.currentTarget.closest('svg') as SVGSVGElement | null;
                              if (!svgElement) return;
                              
                              const pos = calculateTooltipPosition(x2, y2, svgElement, containerRect);
                              
                              setHoveredMonthIndex(index);
                              setTooltipPosition({
                                x: pos.x,
                                y: pos.y
                              });
                            }
                          }}
                          onTouchEnd={() => {
                            setTimeout(() => {
                              setHoveredMonthIndex(null);
                              setTooltipPosition(null);
                            }, 2000);
                          }}
                        />
                      );
                    })}

                    {/* Clickable area for the first point */}
                    {monthlySpend.length > 0 && (() => {
                      const x = 40;
                      const y = chartHeight - 15 - ((monthlySpend[0].amount / maxAmount) * (chartHeight - 30));
                      
                      // Helper function to calculate tooltip position
                      const calculateTooltipPosition = (pointX: number, pointY: number, svgElement: SVGSVGElement, containerRect: DOMRect) => {
                        const svgRect = svgElement.getBoundingClientRect();
                        const svgViewBox = svgElement.viewBox.baseVal;
                        
                        // Calculate scale factors based on actual rendered size vs viewBox
                        const scaleX = svgRect.width / svgViewBox.width;
                        const scaleY = svgRect.height / svgViewBox.height;
                        
                        // Convert viewBox coordinates to actual pixel position within SVG
                        const actualX = (pointX / svgViewBox.width) * svgRect.width;
                        const actualY = (pointY / svgViewBox.height) * svgRect.height;
                        
                        // Position relative to container (accounting for SVG position within container)
                        const containerX = actualX + (svgRect.left - containerRect.left);
                        const containerY = actualY + (svgRect.top - containerRect.top);
                        
                        return { x: containerX, y: containerY };
                      };
                      
                      return (
                        <circle
                          cx={x}
                          cy={y}
                          r="15"
                          fill="transparent"
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => {
                            if (chartContainerRef.current) {
                              const containerRect = chartContainerRef.current.getBoundingClientRect();
                              const svgElement = e.currentTarget.closest('svg') as SVGSVGElement | null;
                              if (!svgElement) return;
                              
                              const svgRect = svgElement.getBoundingClientRect();
                              const svgViewBox = svgElement.viewBox.baseVal;
                              
                              const scaleX = svgRect.width / svgViewBox.width;
                              const scaleY = svgRect.height / svgViewBox.height;
                              
                              const pixelX = (x / svgViewBox.width) * svgRect.width;
                              const pixelY = (y / svgViewBox.height) * svgRect.height;
                              
                              const containerX = pixelX + (svgRect.left - containerRect.left);
                              const containerY = pixelY + (svgRect.top - containerRect.top);
                              
                              setHoveredMonthIndex(0);
                              setTooltipPosition({
                                x: containerX,
                                y: containerY 
                              });
                            }
                          }}
                          onMouseLeave={() => {
                            setHoveredMonthIndex(null);
                            setTooltipPosition(null);
                          }}
                          onTouchStart={(e) => {
                            if (chartContainerRef.current) {
                              const containerRect = chartContainerRef.current.getBoundingClientRect();
                              const svgElement = e.currentTarget.closest('svg') as SVGSVGElement | null;
                              if (!svgElement) return;
                              
                              const svgRect = svgElement.getBoundingClientRect();
                              const svgViewBox = svgElement.viewBox.baseVal;
                              
                              const scaleX = svgRect.width / svgViewBox.width;
                              const scaleY = svgRect.height / svgViewBox.height;
                              
                              const actualX = (x / svgViewBox.width) * svgRect.width;
                              const actualY = (y / svgViewBox.height) * svgRect.height;
                              
                              const containerX = actualX + (svgRect.left - containerRect.left);
                              const containerY = actualY + (svgRect.top - containerRect.top);
                              
                              setHoveredMonthIndex(0);
                              setTooltipPosition({
                                x: containerX,
                                y: containerY 
                              });
                            }
                          }}
                          onTouchEnd={() => {
                            setTimeout(() => {
                              setHoveredMonthIndex(null);
                              setTooltipPosition(null);
                            }, 2000);
                          }}
                        />
                      );
                    })()}

                    {/* Month labels at bottom */}
                    {monthlySpend.map((data, index) => {
                      const x = 40 + (index * (chartWidth - 60) / (monthlySpend.length - 1 || 1));
                      const showLabel = index % 3 === 0 || index === monthlySpend.length - 1;
                      
                      return showLabel ? (
                        <text
                          key={`label-${index}`}
                          x={x}
                          y={chartHeight - 2}
                          textAnchor="middle"
                          fontSize="9"
                          fill="rgba(255, 255, 255, 0.8)"
                          fontWeight="500"
                        >
                          {data.month.split(' ')[0]}
                        </text>
                      ) : null;
                    })}
                  </svg>
  
                  {/* Enhanced Tooltip */}
                  {hoveredMonthIndex !== null && tooltipPosition && (
                    <div
                      className="absolute bg-white text-gray-900 px-3 py-2 rounded-lg shadow-xl text-xs font-medium z-10 border border-gray-200"
                      style={{
                        left: `${tooltipPosition.x}px`,
                        top: `${tooltipPosition.y}px`,
                        transform: 'translate(-50%, -110%)',
                        pointerEvents: 'none',
                        minWidth: '120px'
                      }}
                    >
                      {/* Tooltip arrow pointing down to the point */}
                      <div
                        className="absolute left-1/2 top-full transform -translate-x-1/2"
                        style={{
                          width: 0,
                          height: 0,
                          borderLeft: '6px solid transparent',
                          borderRight: '6px solid transparent',
                          borderTop: '6px solid white'
                        }}
                      />
                      <div className="text-center relative z-10">
                        <div className="font-bold text-sm text-gray-900 mb-1">
                          {monthlySpend[hoveredMonthIndex].month}
                        </div>
                        <div className="text-blue-600 font-semibold">
                          {monthlySpend[hoveredMonthIndex].amount} {monthlySpend[hoveredMonthIndex].amount === 1 ? 'filing' : 'filings'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Balance Card - Takes 1 column, redesigned */}
          {permissions['wallet_view_balance'] && (
            <div className="company-profile-card" data-tour="step-2" style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column' }}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base" style={{ margin: '0' }}>Balance</h2>
                {permissions['wallet_top_up'] && (
                  <button
                    onClick={handleCreditBalance}
                    className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-blue-400 hover:bg-blue-50 rounded-md transition-colors"
                    title="Top up"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Top up</span>
                  </button>
                )}
              </div>
              <div className="mt-auto">
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {creditBalanceLoading ? (
                    <span className="text-sm text-gray-500">Loading...</span>
                  ) : (
                    `SGD ${currencyBalance.toFixed(2)}`
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Free Credits Card - Takes 1 column, redesigned */}
          {permissions['wallet_view_balance'] && (
            <div className="company-profile-card" data-tour="step-3" style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column' }}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base" style={{ margin: '0' }}>Free Credits</h2>
                <div className="flex items-center gap-2">
                  {/* Referral icon button */}
                  <button
                    onClick={copyReferralCode}
                    className="relative flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-blue-400 hover:bg-blue-50 rounded-md transition-colors group"
                    disabled={!referralCode}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {copySuccess ? (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      )}
                    </svg>
                    <span>Referral</span>
                    {/* Tooltip on hover */}
                    <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-green-700 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-20">
                      Share and earn free credits when other purchase
                      {/* Tooltip arrow */}
                      <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                        <span className="block w-0 h-0 border-4 border-transparent border-t-gray-800"></span>
                      </span>
                    </span>
                    {copySuccess && (
                      <span className="absolute -top-7 right-0 bg-blue-600 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20 shadow-md">
                        Copied!
                      </span>
                    )}
                  </button>
                </div>
              </div>
              <div className="mt-auto">
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  {creditBalanceLoading ? (
                    <span className="text-sm text-gray-500">Loading...</span>
                  ) : (
                    `${credit}`
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Header with Title and Action Buttons */}
        {/* All Filings Card - Combined Filter and Table */}
        <div className="filing-card">
          {/* Filter Section - 2 Rows */}
          <div className="space-y-3 mb-4 pb-3 border-b border-gray-200">
            {/* Row 1: Left side (Groups) and Right side (Upload, Search) */}
            <div className="flex items-start gap-4">
              {/* Left side - Groups (can wrap to multiple rows) */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap flex-shrink-0">
                    Group:
                  </label>
                  <button
                    onClick={() => {
                      setSelectedGroupId(null);
                      setCurrentPage(1);
                      setSelectedFilings(new Set());
                    }}
                    className={`f-btn ${selectedGroupId === null ? 'f-btn-light-blue f-btn-xs' : 'f-btn-soft f-btn-xs'}`}
                  >
                    All
                  </button>
                  {groupsLoading ? (
                    <div className="px-3 py-1.5 text-sm text-gray-500">Loading...</div>
                  ) : (
                    groups.map((group) => (
                      <button
                        key={group.id}
                        onClick={() => {
                          setSelectedGroupId(group.id);
                          setCurrentPage(1);
                          setSelectedFilings(new Set());
                        }}
                        className={`f-btn ${selectedGroupId === group.id ? 'f-btn-light-blue f-btn-xs' : 'f-btn-soft f-btn-xs'}`}
                      >
                        {group.name} 
                      </button>
                    ))
                  )}
                </div>
              </div>

            {/* Right side - Refresh, Upload, Search, Advanced Filter, Clear Filters */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Refresh Button */}
              <button
                onClick={() => {
                  setIsRefreshing(true);
                  setRefreshTrigger(prev => prev + 1);
                }}
                disabled={isRefreshing}
                className="f-btn f-btn-success"
                title="Refresh filings list"
              >
                <svg 
                  className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
              
              {/* Upload Button */}
              {permissions['file_upload'] && (
                <button
                  onClick={() => handleFileUpload()}
                  className="f-btn f-btn-primary"
                  data-tour="step-4"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  Upload
                </button>
              )}

              {/* Search */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-40"
                />
              </div>

              {/* Advanced Filter */}
              <div className="relative" ref={filterPanelRef}>
                <button
                  type="button"
                  onClick={openFilterPanel}
                  className={`f-btn f-btn-xs ${hasAdvancedFilters ? 'f-btn-primary' : 'f-btn-outline'}`}
                  title="Advanced filters"
                  aria-expanded={showFilterPanel}
                  aria-label="Advanced filters"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </button>

                {showFilterPanel && (
                  <div className="absolute right-0 top-full mt-2 z-30 w-72 rounded-lg border border-gray-200 bg-white shadow-lg p-4 space-y-3">
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        Status
                      </div>
                      <select
                        value={draftStatusFilter ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraftStatusFilter(v === '' ? null : v);
                        }}
                        className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        aria-label="Status type"
                      >
                        <option value="">All statuses</option>
                        <option value="processing">Processing</option>
                        <option value="converted">Converted</option>
                        <option value="validated">Validated</option>
                        <option value="draft">Draft</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Start date
                        </div>
                        <input
                          type="date"
                          value={draftCreatedFrom}
                          onChange={(e) => setDraftCreatedFrom(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          aria-label="Created from"
                        />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          End date
                        </div>
                        <input
                          type="date"
                          value={draftCreatedTo}
                          onChange={(e) => setDraftCreatedTo(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          aria-label="Created to"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2 pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          setDraftStatusFilter(null);
                          setDraftCreatedFrom('');
                          setDraftCreatedTo('');
                        }}
                        className="f-btn f-btn-soft f-btn-xs"
                      >
                        Reset
                      </button>
                      <button
                        type="button"
                        onClick={applyAdvancedFilters}
                        className="f-btn f-btn-primary f-btn-xs"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

            {/* Row 2: View (Active/Archived) + Clear Filters (right) */}
            {(permissions['file_archive'] || hasActiveFilters) && (
              <div className="flex items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                  {permissions['file_archive'] && (
                    <>
                      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                        View:
                      </label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setShowArchived(false);
                            setStatusFilter(null);
                            setCurrentPage(1);
                            setSelectedFilings(new Set());
                          }}
                          className={`f-btn ${!showArchived ? 'f-btn-light-blue f-btn-xs' : 'f-btn-soft f-btn-xs'}`}
                        >
                          Active
                        </button>
                        <button
                          onClick={() => {
                            setShowArchived(true);
                            setStatusFilter(null);
                            setCurrentPage(1);
                            setSelectedFilings(new Set());
                          }}
                          className={`f-btn ${showArchived ? 'f-btn-light-blue f-btn-xs' : 'f-btn-soft f-btn-xs'}`}
                        >
                          Archived
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="f-btn f-btn-danger f-btn-xs"
                      title="Clear search and advanced filters"
                    >
                      Clear Filters
                    </button>
                  )}
                  {permissions['file_archive'] && selectedFilings.size > 0 && (
                    showArchived ? (
                      <button 
                        className="f-btn f-btn-success f-btn-xs"
                        onClick={() => handleUnarchiveFiling()}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Unarchive ({selectedFilings.size})
                      </button>
                    ) : (
                      <button 
                        className="f-btn f-btn-danger f-btn-xs"
                        onClick={() => handleArchiveFiling()}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Archive ({selectedFilings.size})
                      </button>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          {(currentDocuments.length > 0 || loading) ? (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
              <table className="filing-table">
              <thead>
                <tr>
                  <th className="px-3 py-2 w-12 text-center">
                    <input
                      type="checkbox"
                      checked={currentDocuments.length > 0 && selectedFilings.size === currentDocuments.length}
                      onChange={handleSelectAll}
                      className="w-4 h-4 accent-black border-gray-400 rounded focus:ring-0"
                    />
                  </th>
                  <th className="px-3 py-2 w-1/6">Company Name</th>
                  <th className="px-3 py-2 w-1/6">Filing Type</th>
                  <th className="px-3 py-2 w-1/6">Year End</th>
                  <th className="px-3 py-2 w-1/6">Status</th>
                  <th className="px-3 py-2 w-1/6">Created By</th>
                  <th className="px-3 py-2 w-1/6">Updated By</th>
                  <th className="px-3 py-2 text-center w-1/6">Actions</th>
                </tr>
              </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : currentDocuments.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12">
                        <div className="flex flex-col items-center justify-center text-center space-y-3">
                          <svg 
                            className="w-16 h-16 text-gray-300" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path 
                              strokeLinecap="round" 
                              strokeLinejoin="round" 
                              strokeWidth="1.5" 
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                            />
                          </svg>
                          <div>
                            <p className="text-base font-medium text-gray-700 mb-1">
                              {hasActiveFilters
                                ? 'No filings match your filters'
                                : selectedGroupId
                                  ? 'No filings in this group'
                                  : 'No filings found'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {hasActiveFilters
                                ? 'Try adjusting the status or date range, or click Clear Filters above.'
                                : selectedGroupId
                                  ? 'Assign filings to this group to see them here.'
                                  : 'Upload your first filing to get started.'}
                            </p>
                          </div>
                          {hasActiveFilters ? (
                            <button
                              type="button"
                              onClick={clearFilters}
                              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
                            >
                              Clear Filters
                            </button>
                          ) : !selectedGroupId && permissions['file_upload'] ? (
                            <button
                              onClick={() => handleFileUpload()}
                              className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                            >
                              Upload Filing
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>                
              ) : (
                    currentDocuments.map((doc, idx) => {
                      const assignedGroup = doc.group_id ? groups.find(g => g.id === doc.group_id) : null;
                      return (
                        <tr
                          key={doc.id}
                          className={`${
                            doc.pinned_at 
                              ? "bg-yellow-50 border-l-4 border-yellow-400" // Pinned styling
                              : idx % 2 === 0 ? "bg-white" : "bg-gray-50"    // Normal alternating
                          } hover:bg-gray-100 transition`}
                        >
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedFilings.has(doc.id)}
                              onChange={() => handleSelectFiling(doc.id)}
                              className="w-4 h-4 accent-black text-gray-900 rounded focus:ring-0"
                            />
                          </td>
                          <td className={`px-3 py-2 font-medium ${
                            doc.company_name === 'Analysing file...' 
                              ? 'text-gray-500 italic' 
                              : 'text-gray-800'
                          }`}>
                            <div className="group relative">
                              <div className="truncate max-w-[180px]">
                                {doc.company_name}
                              </div>
                              {/* Tooltip popup on hover - appears above */}
                              {doc.company_name.length > 16 && (
                                <div className="invisible group-hover:visible absolute z-10 bg-gray-500 text-white text-xs rounded py-1 px-2 left-0 bottom-full mb-1 whitespace-normal max-w-sm">
                                  {doc.company_name}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              {getSgFilingTypeDisplay(doc)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600">{doc.year_end}</td>
                          <td className="px-3 py-2">
                            {doc.status === 'processing' ? (
                              // Processing status badge with progress bar (same size as other badges)
                              <div 
                                className="relative inline-flex items-center justify-center"
                                style={{
                                  position: 'relative',
                                  minWidth: '90px',
                                  height: '24px',
                                  fontSize: '10px',
                                  fontWeight: '500',
                                  padding: '0 8px',
                                  borderRadius: '9999px',
                                  overflow: 'hidden',
                                  border: '1px solid #0083B3',
                                  color: '#0083B3',
                                  cursor: 'pointer',
                                  backgroundColor: '#fff'
                                }}
                                title={`Processing: ${doc.processing_status?.current_step || 'Starting...'}`}
                              >
                                {/* Progress bar fill (straight, no rounded corners) */}
                                <div 
                                  style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    height: '100%',
                                    backgroundColor: '#0083B3',
                                    opacity: 0.15,
                                    width: `${PROCESSING_STEPS[doc.processing_status?.current_step || 'raw_upload'] || 0}%`,
                                    transition: 'width 0.5s ease'
                                  }}
                                />
                                {/* Percentage text */}
                                <span style={{ position: 'relative', zIndex: 1 }}>
                                  {PROCESSING_STEPS[doc.processing_status?.current_step || 'raw_upload'] || 0}%
                                </span>
                              </div>
                            ) : (
                              // Regular status badge (same size as processing bar)
                              <span style={getStatusColor(doc.status)}>
                                {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-col">
                              <span className="text-gray-700 font-medium">{doc.created_by_name}</span>
                              <span className="text-gray-500 text-xs">{dayjs(doc.created_at).fromNow()}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-col">
                              <span className="text-gray-700 font-medium">{doc.updated_by_name || doc.created_by_name}</span>
                              <span className="text-gray-500 text-xs">{formatDate(doc.updated_at)}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex justify-center items-center space-x-1">
                              {/* Pin button - keep visible */}
                              {permissions['file_pinned'] && (
                                <button 
                                  className={`
                                    p-1.5 rounded-md transition-colors
                                    ${doc.pinned_at 
                                      ? 'text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50' 
                                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                    }
                                  `}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTogglePin(doc.id, doc.pinned_at || null);
                                  }}
                                  title={doc.pinned_at ? 'Unpin' : 'Pin'}
                                >
                                  {doc.pinned_at ? (
                                    // Filled star
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                  ) : (
                                    // Outline star
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                    </svg>
                                  )}
                                </button>
                              )}
                              {/* Edit button - text only */}
                              {permissions['file_edit'] && (
                                <Link
                                  to={`/all-filings/${companyId}/edit/${doc.id}`}
                                  className={`px-2 py-1 text-xs rounded border transition ${
                                    doc.status === 'processing'
                                      ? 'border-gray-200 text-gray-400 cursor-not-allowed pointer-events-none'
                                      : 'border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (doc.status === 'processing') {
                                      e.preventDefault();
                                    }
                                  }}
                                  title={doc.status === 'processing' ? 'Not available while processing' : 'Edit'}
                                >
                                  Edit
                                </Link>
                              )}
                              {/* View button - text only */}
                              {permissions['file_view'] && (
                                <Link
                                  to={`/all-filings/${companyId}/view/${doc.id}`}
                                  className={`px-2 py-1 text-xs rounded border transition ${
                                    doc.status === 'processing' || doc.status === 'converted'
                                      ? 'border-gray-200 text-gray-400 cursor-not-allowed pointer-events-none'
                                      : 'border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (doc.status === 'processing' || doc.status === 'converted') {
                                      e.preventDefault();
                                    }
                                  }}
                                  title={
                                    doc.status === 'processing' 
                                      ? 'Not available while processing' 
                                      : doc.status === 'converted'
                                      ? 'Not available for converted files'
                                      : 'View'
                                  }
                                >
                                  View
                                </Link>
                              )}
                              {/* Download button - keep visible if user has permission */}
                              {permissions['file_download'] && (
                                <button 
                                  className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPendingDownloadDoc(doc);
                                    setShowDownloadTipModal(true);
                                  }}
                                  disabled={
                                    downloadingFilingId === doc.id || 
                                    doc.status === 'processing' || 
                                    doc.status === 'converted'
                                  }
                                  title={
                                    doc.status === 'processing'
                                      ? 'Not available while processing'
                                      : doc.status === 'converted'
                                      ? 'Not available for converted files'
                                      : 'Download XBRL'
                                  }
                                >
                                  {downloadingFilingId === doc.id ? 'Downloading...' : 'Download'}
                                </button>
                              )}
                              {/* Reassign button - only show for admins */}
                              {permissions['file_reassign'] && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const assignedGroup = doc.group_id ? allGroups.find(g => g.id === doc.group_id) : null;
                                    setTempSelectedGroups(doc.group_id ? [doc.group_id] : []);
                                    setShowGroupAssignmentModal(doc.id);
                                    // Fetch all groups when opening the modal
                                    fetchAllGroups();
                                  }}
                                  className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50 transition"
                                  title="Reassign group"
                                >
                                  Reassign
                                </button>
                              )}
                              {/* Delete button - text only */}
                              {permissions['file_delete'] && (
                                <button
                                  className="px-2 py-1 text-xs rounded border border-red-300 text-red-700 hover:border-red-400 hover:bg-red-50 transition"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDeleteFilingConfirm(doc.id);
                                  }}
                                >
                                Delete
                              </button>
                              )}
                            </div>
                          </td>                      
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            /* Empty state - no table, just a clean card */
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="flex flex-col items-center justify-center text-center py-16 px-4">
                <svg 
                  className="w-16 h-16 text-gray-300 mb-4" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="1.5" 
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
                  />
                </svg>
                <p className="text-base font-medium text-gray-700 mb-1">
                  {hasActiveFilters
                    ? 'No filings match your filters'
                    : selectedGroupId
                      ? 'No filings in this group'
                      : 'No filings found'}
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  {hasActiveFilters
                    ? 'Try adjusting the status or date range, or click Clear Filters above.'
                    : selectedGroupId
                      ? 'Assign filings to this group to see them here.'
                      : 'Upload your first filing to get started.'}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          )}

          {!loading && filteredDocuments.length > 0 && (
            <div className="flex items-center justify-between text-sm text-gray-600 mt-4 mb-4">
              <div className="flex items-center space-x-4">
                <span>Rows per page:</span>
                <select 
                  value={rowsPerPage} 
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                </select>
              </div>
              <div className="flex flex-col items-center">
                <span>
                  {startIndex + 1}-{Math.min(endIndex, filteredDocuments.length)} of {filteredDocuments.length}
                </span>
                {documents.length !== filteredDocuments.length && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    (filtered from {documents.length} total)
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                  >
                    &lt;
                  </button>
                  
                  {/* Page Numbers with Ellipsis */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className="f-btn f-btn-primary f-btn-xs"
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return <span key={page} className="px-2">...</span>;
                    }
                    return null;
                  })}
                  
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="f-btn f-btn-soft f-btn-xs"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </div>
          )}

          </div> {/* End filing-card */}
        </div>
      </div>

      {/* Group Assignment Modal for Filings */}
      {showGroupAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    Assign Group
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">Select a group for this filing</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowGroupAssignmentModal(null);
                  setTempSelectedGroups([]);
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="space-y-1.5 max-h-80 overflow-y-auto pr-2">
                {/* Unassigned option */}
                <label
                  className={`flex items-start space-x-3 p-4 rounded-lg cursor-pointer transition-all ${
                    tempSelectedGroups.length === 0
                      ? 'bg-blue-50 border-2 border-blue-200 shadow-sm'
                      : 'bg-white border-2 border-gray-100 hover:border-blue-200 hover:bg-blue-50/50'
                  }`}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <div className={`relative w-5 h-5 border-2 rounded flex items-center justify-center transition-all ${
                      tempSelectedGroups.length === 0
                        ? 'border-blue-500 bg-white'
                        : 'border-gray-300 bg-white'
                    }`}>
                      {tempSelectedGroups.length === 0 && (
                        <svg 
                          className="w-3.5 h-3.5 text-blue-500"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth="3"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-semibold ${
                      tempSelectedGroups.length === 0 ? 'text-blue-900' : 'text-gray-900'
                    }`}>
                      Default
                    </span>
                    <p className={`text-xs mt-1 ${
                      tempSelectedGroups.length === 0 ? 'text-blue-700' : 'text-gray-500'
                    }`}>
                      This filing will not be assigned to any group
                    </p>
                  </div>
                  <input
                    type="radio"
                    name="group"
                    checked={tempSelectedGroups.length === 0}
                    onChange={() => setTempSelectedGroups([])}
                    className="sr-only"
                  />
                </label>

                {allGroupsLoading ? (
                  <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-500 mt-2">Loading groups...</p>
                  </div>
                ) : allGroups.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="mx-auto h-12 w-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-sm font-medium text-gray-900 mb-1">No groups available</p>
                    <p className="text-xs text-gray-500">Create a group first to assign filings</p>
                  </div>
                ) : (
                  allGroups.map((group) => {
                    const isSelected = tempSelectedGroups.includes(group.id);
                    
                    return (
                      <label
                        key={group.id}
                        className={`flex items-start space-x-3 p-4 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-blue-50 border-2 border-blue-200 shadow-sm'
                            : 'bg-white border-2 border-gray-100 hover:border-blue-200 hover:bg-blue-50/50'
                        }`}
                      >
                        <div className="flex-shrink-0 mt-0.5">
                          <div className={`relative w-5 h-5 border-2 rounded flex items-center justify-center transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-white'
                              : 'border-gray-300 bg-white'
                          }`}>
                            {isSelected && (
                              <svg 
                                className="w-3.5 h-3.5 text-blue-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="3"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className={`text-sm font-semibold ${
                              isSelected ? 'text-blue-900' : 'text-gray-900'
                            }`}>
                              {group.name}
                            </span>
                          </div>
                          {group.description && (
                            <p className={`text-xs mt-1 ${
                              isSelected ? 'text-blue-700' : 'text-gray-500'
                            }`}>
                              {group.description}
                            </p>
                          )}
                        </div>
                        <input
                          type="radio"
                          name="group"
                          checked={isSelected}
                          onChange={() => setTempSelectedGroups([group.id])}
                          className="sr-only"
                        />
                      </label>
                    );
                  })
                )}
              </div>
              
              <div className="flex items-center justify-end space-x-3 pt-4 mt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowGroupAssignmentModal(null);
                    setTempSelectedGroups([]);
                  }}
                  className="f-btn f-btn-soft"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveGroupAssignments(showGroupAssignmentModal)}
                  className="f-btn f-btn-primary"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success and Error Messages */}
      {successMsg && (
        <div className="fixed top-1 right-4 z-[10000] bg-blue-50 border border-blue-200 px-6 py-3 rounded-lg shadow-sm flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-blue-800">{successMsg}</span>
          <button 
            onClick={() => setSuccessMsg(null)}
            className="ml-3 text-blue-400 hover:text-blue-600"
          >
            ×
          </button>
        </div>
      )}

      {errorMsg && (
        <div className="fixed top-4 right-4 z-[10000] bg-red-50 border border-red-200 px-6 py-3 rounded-lg shadow-sm flex items-center">
          <svg className="w-5 h-5 mr-2 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-red-800">{errorMsg}</span>
          <button 
            onClick={() => setErrorMsg(null)}
            className="ml-3 text-red-400 hover:text-red-600"
          >
            ×
          </button>
        </div>
      )}

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-6 md:p-8">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col my-4">
            {/* Header - Fixed at top */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Upload Filing</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Upload files and assign to a group</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsUploadModalOpen(false);
                  setUploadFiles([]);
                  setUploadGroupId(null);
                  setUploadFilingTypeId('');
                }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white transition-colors flex-shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content - Scrollable */}
            <form onSubmit={(e) => { e.preventDefault(); handleUploadSubmit(); }} className="p-6 space-y-5 overflow-y-auto flex-1">
              {/* Filing Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Filing Type <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <select
                    value={uploadFilingTypeId}
                    onChange={(e) => setUploadFilingTypeId(e.target.value)}
                    required
                    className="w-full appearance-none bg-white border-2 border-gray-200 rounded-lg px-4 py-3 pr-10 text-sm font-medium text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all hover:border-gray-300 cursor-pointer shadow-sm"
                  >
                    <option value="">Select filing type...</option>
                    {filingTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Title */}
              {/* <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Title <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="e.g., Annual Filing 2024"
                />
              </div> */}

              {/* Group Selection - Only show if user has more than one group option */}
              {(() => {
                // Determine available groups for this user
                let availableGroups = groups;
                if (!isUserAdmin) {
                  // Non-admins can only assign to groups they belong to
                  availableGroups = groups.filter(g => userGroupIds.includes(g.id));
                }

                // Hide section if:
                // 1. User is not admin and has no groups (only default available)
                // 2. User is not admin and has only one group (default + 1 group = only 2 options)
                // 3. User is admin but organization has only one or zero groups
                const shouldHideGroupSection = 
                  (!isUserAdmin && (userGroupIds.length === 0 || availableGroups.length <= 1)) ||
                  (isUserAdmin && groups.length <= 1);

                if (shouldHideGroupSection) {
                  // Auto-assign to default (null) if section is hidden
                  if (uploadGroupId !== null) {
                    setUploadGroupId(null);
                  }
                  return null;
                }

                return (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Assign to Group <span className="text-gray-400 text-xs font-normal">(Optional)</span>
                    </label>
                    <div className="space-y-2 max-h-48 overflow-y-auto border-2 border-gray-200 rounded-lg p-3">
                      {/* Unassigned Option - Everyone can see this */}
                      <label className="flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-all hover:bg-gray-50">
                        <div className="flex-shrink-0 mt-0.5">
                          <div className={`relative w-5 h-5 border-2 rounded flex items-center justify-center transition-all ${
                            uploadGroupId === null
                              ? 'border-blue-500 bg-white'
                              : 'border-gray-300 bg-white'
                          }`}>
                            {uploadGroupId === null && (
                              <svg 
                                className="w-3.5 h-3.5 text-blue-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth="3"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold text-gray-900">Default</span>
                          <p className="text-xs text-gray-500 mt-0.5">This filing will not be assigned to any group</p>
                        </div>
                        <input
                          type="radio"
                          name="uploadGroup"
                          checked={uploadGroupId === null}
                          onChange={() => setUploadGroupId(null)}
                          className="sr-only"
                        />
                      </label>

                      {/* Group Options - Filtered based on user role */}
                      {(() => {
                        if (availableGroups.length === 0) {
                          return (
                            <div className="text-sm text-gray-500 py-2">
                              {!isUserAdmin && userGroupIds.length === 0 
                                ? "You are not assigned to any groups. You can only assign filings as 'Default'."
                                : "No groups available"}
                            </div>
                          );
                        }

                        return availableGroups.map((group) => {
                          const isSelected = uploadGroupId === group.id;
                          return (
                            <label
                              key={group.id}
                              className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer transition-all ${
                                isSelected
                                  ? 'bg-blue-50 border-2 border-blue-200'
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className="flex-shrink-0 mt-0.5">
                                <div className={`relative w-5 h-5 border-2 rounded flex items-center justify-center transition-all ${
                                  isSelected
                                    ? 'border-blue-500 bg-white'
                                    : 'border-gray-300 bg-white'
                                }`}>
                                  {isSelected && (
                                    <svg 
                                      className="w-3.5 h-3.5 text-blue-500"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                      stroke="currentColor"
                                      strokeWidth="3"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-semibold text-gray-900">{group.name}</span>
                                {group.description && (
                                  <p className="text-xs text-gray-500 mt-0.5">{group.description}</p>
                                )}
                              </div>
                              <input
                                type="radio"
                                name="uploadGroup"
                                checked={isSelected}
                                onChange={() => setUploadGroupId(group.id)}
                                className="sr-only"
                              />
                            </label>
                          );
                        });
                      })()}
                    </div>
                  </div>
                );
              })()}

              {/* File Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  File <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    id="file-upload"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span className="text-sm font-medium text-gray-700 mb-1">
                      Click to upload file
                    </span>
                    <span className="text-xs text-gray-500">
                      PDF format only
                    </span>
                  </label>
                </div>
                
                {/* Selected File */}
                {uploadFiles.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-sm text-gray-700 truncate">{uploadFiles[0].name}</span>
                        <span className="text-xs text-gray-500">({(uploadFiles[0].size / 1024).toFixed(2)} KB)</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setUploadFiles([]);
                          // Reset the file input
                          const fileInput = document.getElementById('file-upload') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="ml-2 text-red-500 hover:text-red-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {errorMsg && (
                <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-red-700 font-medium">{errorMsg}</p>
                  </div>
                </div>
              )}
              
              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setIsUploadModalOpen(false);
                    setUploadFiles([]);
                    setUploadGroupId(null);
                    setUploadFilingTypeId('');
                  }}
                  className="f-btn f-btn-soft"
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || uploadFiles.length === 0}
                  className="f-btn f-btn-primary"
                >
                  {uploading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span>Upload</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Credit Deduction Confirmation Modal */}
      {showCreditDeductionModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-lg font-semibold mb-4">Credit Deduction Notice</h3>
          <div className="mb-4">
            <p className="text-gray-700 mb-2">
              Uploading this file will deduct credits from your account:
            </p>
            <ul className="list-disc list-inside mb-4 space-y-2">
              {pendingDeductions.freeCredit > 0 && (
                <li><span className="font-semibold">{pendingDeductions.freeCredit}</span> free credit will be deducted</li>
              )}
              {pendingDeductions.currencyBalance > 0 && (
                <li><span className="font-semibold">SGD {pendingDeductions.currencyBalance.toFixed(2)}</span> will be deducted from currency balance</li>
              )}
            </ul>
            <p className="text-red-600 font-medium text-sm">
              ⚠️ This action is non-refundable
            </p>
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setShowCreditDeductionModal(false);
                setPendingUpload(null);
              }}
              className="f-btn f-btn-soft"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmedUpload}
              className="f-btn f-btn-primary"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Confirm & Upload'}
            </button>
          </div>
        </div>
      </div>
    )}

      {/* Insufficient Balance Modal */}
      {showInsufficientBalanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Insufficient Balance</h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-3">
                You don’t have enough <span className="font-semibold">Free Credits</span> or <span className="font-semibold">SGD Balance</span> to upload this filing.
              </p>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Current Free Credits:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    {insufficientInfo?.currentFreeCredit ?? credit}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Current SGD Balance:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    SGD {(insufficientInfo?.currentCurrencyBalance ?? currencyBalance).toFixed(2)}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-red-200">
                  <span className="text-sm text-gray-600">Required (SGD):</span>
                  <span className="text-sm font-semibold text-red-600">
                    SGD {(insufficientInfo?.requiredCurrencyBalance ?? 0).toFixed(2)}
                  </span>
                </div>
              </div>

              {permissions['wallet_top_up'] ? (
                <p className="text-sm text-gray-600">Please top up before uploading.</p>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800 font-medium mb-1">⚠️ You don't have permission to top up</p>
                  <p className="text-sm text-yellow-700">
                    Please contact your organization's Owner/Admin or a team member with top-up permission.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowInsufficientBalanceModal(false);
                  setInsufficientInfo(null);
                }}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>

              {permissions['wallet_top_up'] ? (
                <button
                  onClick={() => {
                    setShowInsufficientBalanceModal(false);
                    setInsufficientInfo(null);
                    setIsPurchaseModalOpen(true);
                    fetchProducts();
                  }}
                  className="f-btn f-btn-primary"
                >
                  Top Up Now
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {showArchiveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Archive Filing(s)</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to archive {selectedFilings.size} filing(s)?
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowArchiveConfirm(false)}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>
              <button
                onClick={confirmArchiveFiling}
                className="f-btn f-btn-danger"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Filing Confirmation Modal */}
      {showDeleteFilingConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Filing</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this filing? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowDeleteFilingConfirm(null)}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleDeleteFiling(showDeleteFilingConfirm);
                  setShowDeleteFilingConfirm(null);
                }}
                className="f-btn f-btn-danger"
              >
                Delete Filing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unarchive Confirmation Modal */}
      {showUnarchiveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Unarchive Filing(s)</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to unarchive {selectedFilings.size} filing(s)? They will be restored to draft status.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowUnarchiveConfirm(false)}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>
              <button
                onClick={confirmUnarchiveFiling}
                className="f-btn f-btn-success"
              >
                Unarchive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Page Count Confirmation Modal */}
      {showPdfPageCountModal && pendingPdfFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm PDF Upload</h3>

            <div className="mb-4">
              <p className="text-gray-700 mb-2">
                Your report only has <span className="font-bold text-red-600">{pendingPdfFile.pageCount} page{pendingPdfFile.pageCount !== 1 ? 's' : ''}</span>.
              </p>
              <p className="text-gray-600 text-sm mb-4">
                Are you sure you want to upload this file? Please verify this is the correct document.
              </p>
              
              <div className="bg-red-50 border-l-4 border-red-400 p-3 rounded">
                  <p className="text-sm text-red-800">
                    <strong>File:</strong> {pendingPdfFile.file.name}
                  </p>
                  <p className="text-sm text-red-800 mt-1">
                    <strong>Size:</strong> {(pendingPdfFile.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelPdfUpload}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPdfUpload}
                className="f-btn f-btn-primary"
              >
                Upload Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download tip modal - show before starting XBRL download */}
      {showDownloadTipModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001] p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden border border-gray-100">
            {/* Header with icon */}
            <div className="bg-blue-50 px-6 py-4 border-b border-blue-100">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Tip: Full Set Statements</h3>
                  <p className="text-xs text-blue-700 mt-0.5">For better formatting in the text block</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              <p className="text-sm text-gray-700 leading-relaxed">
                For the <strong className="text-gray-900">Full Set Statements</strong> section, we suggest copying from your PDF’s <strong className="text-gray-900">Word version</strong> and pasting into the <strong className="text-gray-900">ACRA tools’ Full Set Statement section’s text block</strong>. That way formatting (e.g. double line, totals, spacing) stays the same.
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDownloadTipModal(false)}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDownloadTipConfirm}
                disabled={downloadingFilingId !== null}
                className="f-btn f-btn-success"
              >
                {downloadingFilingId ? 'Generating...' : 'OK and Download'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Indicator */}
      {checkingPdfPages && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-[9998]">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-3 shadow-lg">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-amber-500"></div>
            <span className="text-gray-700">Checking PDF...</span>
          </div>
        </div>
      )}
      <PurchaseCreditModal />
      <PaymentLoadingModal />

    </MainLayout>
  );
}